/**
 * VIP badges — VIP and the account's VIP 等级, VIP1 to VIP10 — in the
 * style 会员等级 picks for the platform (branding.level_badge, the key it
 * had as 用户等级):
 *   standard — 标准盾标, the first one shipped and the default: a shield in
 *              the level's colour with a V on it, VIP and the level in a
 *              pill tucked under it, in Arco's palette (so the dark theme
 *              re-tones it);
 *   facet    — 棱面盾标: a faceted shield and a pill, hand-drawn letters;
 *   holo     — 黑卡炫彩: a black card, iridescent letters;
 *   capsule  — 流光胶囊: a capsule in the level's colour, a crown, a sweep
 *              of light;
 *   tiered   — 进阶套系: facet to VIP2, capsules from VIP3, the black card
 *              with a gold crown from VIP6.
 * Past standard the letters and digits are drawn by badgeGlyphs, so the
 * lettering is the same on every device. Seven tiers of colour,
 * VIP6 and up the last. Shared byte-identical by both consoles: the admin
 * console previews the styles on its 会员等级 page.
 */

import { letters, measure, word } from '@/utils/badgeGlyphs'

export type VipBadgeStyle = 'standard' | 'facet' | 'holo' | 'capsule' | 'tiered'
export const VIP_BADGE_STYLES: VipBadgeStyle[] = ['standard', 'facet', 'holo', 'capsule', 'tiered']

// Each tier's colours, lightest to deepest: grey, green, blue, coral, rose,
// violet, gold.
const TIERS = [
  ['#F4F5F7', '#DDE1E6', '#AEB4BE', '#7D8592'],
  ['#E9FBF4', '#B9EED9', '#3FCB9F', '#0E9C72'],
  ['#EAF2FF', '#C3DAFF', '#4F8EFF', '#1E5FE0'],
  ['#FFF2EC', '#F9D5C6', '#EF8B63', '#D6603A'],
  ['#FFEFF5', '#FBCADC', '#F0729F', '#CF3F75'],
  ['#F5EFFF', '#DDCDFF', '#9C6DF5', '#6B3ED6'],
  ['#FFF8E2', '#FCE4A2', '#F1B431', '#BF8209'],
]
// The iridescent inks on the black card, one per tier, V6 the full spectrum.
const HOLO = [
  ['#F4F6F9', '#C3CAD5', '#F4F6F9'],
  ['#D8FFF1', '#8FE8CF', '#C4ECFF'],
  ['#E2F0FF', '#9EC6FF', '#CDBEFF'],
  ['#FFE5D6', '#FFB79E', '#FFD9F0'],
  ['#FFD9EA', '#FF9EC6', '#DDBDFF'],
  ['#EEDDFF', '#BBA0FF', '#A2CEFF'],
  ['#FFD6E8', '#E6C6FF', '#B6D7FF', '#C6FFEE', '#FFF1C2'],
]
const SHIELD = 'M8 .8 14.8 3.3V8.6c0 4.2-2.9 7.4-6.8 8.8C4.1 16 1.2 12.8 1.2 8.6V3.3Z'
const SHIELD_V = 'M4.7 6.1h2.1L8 9.7l1.2-3.6h2.1L8.9 12.4H7.1z'
const CROWN = 'M.6 7.4 0 1.6l2.8 2.2L5 .4l2.2 3.4L10 1.6l-.6 5.8z'
const STAR = 'l.7 1.6 1.6.7-1.6.7-.7 1.6-.7-1.6-1.6-.7 1.6-.7z'

class Ids {
  private n = 0
  constructor(private prefix: string) {}
  next() {
    return `${this.prefix}-${this.n++}`
  }
}

const stops = (colors: string[]) =>
  colors.map((c, i) => `<stop offset="${(i / Math.max(1, colors.length - 1)).toFixed(3)}" stop-color="${c}"/>`).join('')

function gradient(ids: Ids, colors: string[], attrs: string) {
  const id = ids.next()
  return { id, def: `<linearGradient id="${id}" ${attrs}>${stops(colors)}</linearGradient>` }
}

// A gradient across a whole word, in the letters' own grid units.
function inkAcross(ids: Ids, colors: string[], s: string) {
  const id = ids.next()
  return { id, def: `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${word(s).w}" y2="8">${stops(colors)}</linearGradient>` }
}

// The faceted shield at x: its light facet over its deep one, the V cut
// through or laid in white.
function shield(ids: Ids, t: number, x: number, whiteV: boolean) {
  const [pale, light, mid, deep] = TIERS[t]
  const clip = ids.next()
  const mask = ids.next()
  const lit = gradient(ids, [pale, light], 'x1="0" y1="0" x2="0" y2="1"')
  const dark = gradient(ids, [mid, deep], 'x1="0" y1="0" x2="1" y2="1"')
  let defs = lit.def + dark.def + `<clipPath id="${clip}"><path d="${SHIELD}"/></clipPath>`
  if (!whiteV) {
    defs += `<mask id="${mask}" maskUnits="userSpaceOnUse" x="-2" y="-2" width="20" height="22"><rect x="-2" y="-2" width="20" height="22" fill="#fff"/><path d="${SHIELD_V}" fill="#000"/></mask>`
  }
  const body =
    `<g ${whiteV ? '' : `mask="url(#${mask})"`}><g clip-path="url(#${clip})">` +
    `<rect width="16" height="18" fill="url(#${lit.id})"/><path d="M15 3.2V18H0v-5.8z" fill="url(#${dark.id})"/>` +
    `<path d="M0 0h8v18H0z" fill="#fff" opacity=".12"/></g></g>` +
    (whiteV ? `<path d="${SHIELD_V}" fill="#fff"/>` : '') +
    `<path d="${SHIELD}" fill="none" stroke="#fff" stroke-opacity=".35" stroke-width=".7"/>`
  return { defs, g: `<g transform="translate(${x} 0)">${body}</g>` }
}

// A sweep of light across a capsule W × H (.vipb-shine animates it).
function sweep(ids: Ids, W: number, H: number, alpha: number) {
  const clip = ids.next()
  const g = gradient(ids, ['rgba(255,255,255,0)', `rgba(255,255,255,${alpha})`, 'rgba(255,255,255,0)'], 'x1="0" y1="0" x2="1" y2="0"')
  return {
    defs: g.def + `<clipPath id="${clip}"><rect width="${W.toFixed(2)}" height="${H}" rx="${H / 2}"/></clipPath>`,
    g: `<g clip-path="url(#${clip})"><g class="vipb-shine"><rect x="-14" y="-2" width="7" height="${H + 4}" fill="url(#${g.id})" transform="skewX(-20)"/></g></g>`,
  }
}

function svg(W: number, H: number, scale: number, defs: string, body: string) {
  return `<svg width="${(W * scale).toFixed(1)}" height="${(H * scale).toFixed(1)}" viewBox="0 0 ${W.toFixed(2)} ${H}" style="display:block;overflow:visible" aria-hidden="true"><defs>${defs}</defs>${body}</svg>`
}

// 标准盾标: Arco's palettes, one a tier, read from the page's own tokens.
const PALETTES = ['gray', 'green', 'arcoblue', 'orangered', 'red', 'purple', 'gold']

/**
 * The colour of VIP level n — the standard badge's, in Arco's palette so
 * the dark theme re-tones it, and the same hue every style's tier has —
 * for what goes with the badge, as its 成长值's bar.
 */
export function vipColor(level: number): string {
  return `rgb(var(--${PALETTES[Math.min(Math.max(0, Math.floor(level || 0)), PALETTES.length - 1)]}-6))`
}

function standard(ids: Ids, n: number, t: number, scale: number) {
  const tone = PALETTES[t]
  const main = `var(--${tone}-6)`
  // Gold's 6 is too light to read on white: its words take the 7.
  const ink = `var(--${tone}-${tone === 'gold' ? 7 : 6})`
  const fill = ids.next()
  const px = (v: number) => `${+(v * scale).toFixed(2)}px`
  const mark =
    `<svg width="${px(16)}" height="${px(18)}" viewBox="0 0 16 18" style="position:relative;z-index:1;display:block;flex:none" aria-hidden="true">` +
    `<defs><linearGradient id="${fill}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:rgb(var(--${tone}-4))"/><stop offset="1" style="stop-color:rgb(var(--${tone}-7))"/></linearGradient></defs>` +
    `<path fill="url(#${fill})" d="M8 .6l6.7 2.5v5.3c0 4.2-2.8 7.5-6.7 9-3.9-1.5-6.7-4.8-6.7-9V3.1z"/>` +
    `<path d="M8 1.9l5.5 2v4.5c0 3.5-2.2 6.3-5.5 7.7-3.3-1.4-5.5-4.2-5.5-7.7V3.9z" fill="none" stroke="rgba(255,255,255,.45)" stroke-width=".8"/>` +
    `<path fill="#fff" d="M4.6 5.6h2l1.4 4.1 1.4-4.1h2l-2.5 6.6H7.1z"/></svg>`
  const pill =
    `<span style="box-sizing:border-box;height:${px(14)};margin-left:${px(-7)};padding:0 ${px(6)} 0 ${px(10)};` +
    `border:${px(1)} solid rgba(${main},.35);border-radius:${px(7)};font-size:${px(11)};font-style:italic;font-weight:600;` +
    `line-height:${px(12)};letter-spacing:${px(0.2)};white-space:nowrap;color:rgb(${ink});background:rgba(${main},.1)">VIP${n}</span>`
  return `<span style="display:inline-flex;align-items:center;height:${px(18)}">${mark}${pill}</span>`
}

function facet(ids: Ids, n: number, t: number, scale: number) {
  const [pale, light, mid, deep] = TIERS[t]
  const label = 'VIP' + n
  const h = 7.2
  const x = 10
  const pillH = 13.6
  const y = 2.2
  const pillW = 12.5 + measure(label, h)
  const W = x + pillW + 0.4
  const fill = gradient(ids, [pale, light], 'x1="0" y1="0" x2="1" y2="0"')
  const s = shield(ids, t, 0, true)
  return svg(W, 18, scale, fill.def + s.defs,
    `<rect x="${x}" y="${y}" width="${pillW.toFixed(2)}" height="${pillH}" rx="${pillH / 2}" fill="url(#${fill.id})" stroke="${mid}" stroke-opacity=".35" stroke-width=".8"/>` +
      letters(label, x + 7.5, y + (pillH - h) / 2, h, deep, 1.55) + s.g)
}

function holo(ids: Ids, n: number, t: number, scale: number) {
  const label = 'VIP' + n
  const H = 17
  const h = 8.2
  const star = t >= 5 ? 5 : 0
  const W = 14 + measure(label, h) + star
  const ink = HOLO[t]
  const card = gradient(ids, ['#26195A', '#0D0726'], 'x1="0" y1="0" x2="1" y2="1"')
  const letter = inkAcross(ids, ink, label)
  const rim = gradient(ids, [ink[0], ink[Math.floor(ink.length / 2)], ink[ink.length - 1]], 'x1="0" y1="0" x2="1" y2="0"')
  return svg(W, H, scale, card.def + letter.def + rim.def,
    `<rect x=".4" y=".4" width="${(W - 0.8).toFixed(2)}" height="${H - 0.8}" rx="${(H - 0.8) / 2}" fill="url(#${card.id})" stroke="url(#${rim.id})" stroke-opacity=".55" stroke-width=".8"/>` +
      letters(label, 7, (H - h) / 2, h, `url(#${letter.id})`, 1.6) +
      (star ? `<path d="M${(W - 6).toFixed(2)} 3.2${STAR}" fill="#FFF3D1"/>` : ''))
}

function capsule(ids: Ids, n: number, t: number, scale: number, shine: boolean) {
  const [, , mid, deep] = TIERS[t]
  const label = 'VIP' + n
  const H = 16
  const h = 7.4
  const W = 24.5 + measure(label, h)
  const fill = gradient(ids, [mid, deep], 'x1="0" y1="0" x2="1" y2="1"')
  const light = sweep(ids, W, H, 0.75)
  return svg(W, H, scale, fill.def + light.defs,
    `<rect width="${W.toFixed(2)}" height="${H}" rx="${H / 2}" fill="url(#${fill.id})"/>` +
      `<rect x=".6" y=".6" width="${(W - 1.2).toFixed(2)}" height="${H / 2 - 0.6}" rx="${H / 2 - 0.6}" fill="#fff" opacity=".18"/>` +
      `<path d="${CROWN}" transform="translate(6 4.3)" fill="#fff"/>` +
      letters(label, 18.5, (H - h) / 2, h, '#fff', 1.55) +
      (shine && t > 0 ? light.g : ''))
}

// 进阶套系's top: the black card, a gold crown, iridescent letters, a star
// and the sweep.
function crowned(ids: Ids, n: number, scale: number) {
  const label = 'VIP' + n
  const H = 17
  const h = 8.2
  const W = 29.5 + measure(label, h)
  const card = gradient(ids, ['#2A1B5E', '#0B0620'], 'x1="0" y1="0" x2="1" y2="1"')
  const letter = inkAcross(ids, HOLO[6], label)
  const gold = gradient(ids, ['#FFE7A3', '#E9B23B'], 'x1="0" y1="0" x2="0" y2="1"')
  const rim = gradient(ids, ['#FFE7A3', '#B6D7FF', '#FFD6E8'], 'x1="0" y1="0" x2="1" y2="0"')
  const light = sweep(ids, W, H, 0.55)
  return svg(W, H, scale, card.def + letter.def + gold.def + rim.def + light.defs,
    `<rect x=".4" y=".4" width="${(W - 0.8).toFixed(2)}" height="${H - 0.8}" rx="${(H - 0.8) / 2}" fill="url(#${card.id})" stroke="url(#${rim.id})" stroke-width=".8"/>` +
      `<path d="${CROWN}" transform="translate(6 4.6)" fill="url(#${gold.id})"/>` +
      letters(label, 18.5, (H - h) / 2, h, `url(#${letter.id})`, 1.6) +
      `<path d="M${(W - 6.5).toFixed(2)} 3.4${STAR}" fill="#FFF3D1"/>` + light.g)
}

/**
 * The badge for VIP level n in style — an unknown style is standard, as
 * the server's default — at scale times its size (18px high). prefix keeps
 * its gradient and mask ids apart from every other badge on the page.
 */
export function vipBadgeSvg(style: string | undefined, level: number, scale = 1, prefix = 'vipb'): string {
  const n = Math.max(0, Math.floor(level || 0))
  const t = Math.min(n, TIERS.length - 1)
  const ids = new Ids(prefix)
  switch (style) {
    case 'facet':
    case 'classic':
      return facet(ids, n, t, scale)
    case 'holo':
      return holo(ids, n, t, scale)
    case 'capsule':
      return capsule(ids, n, t, scale, true)
    case 'tiered':
      if (n <= 2) return facet(ids, n, t, scale)
      if (n <= 5) return capsule(ids, n, t, scale, false)
      return crowned(ids, n, scale)
    default:
      return standard(ids, n, t, scale)
  }
}
